/**
 * @fileoverview useTransactionCalculations - Transaction math and budget allocations.
 *
 * Performs all financial calculations for transactions including totals, spending
 * by category, monthly allocations, and template-based income distribution.
 *
 * @description This hook provides:
 * - Transaction total calculation (net balance for month)
 * - Spending breakdown by category
 * - Monthly allocation tracking (max vs allocated vs remaining)
 * - Template-based income allocation algorithm
 *
 * Calculation logic:
 * - Total: Sum of all transaction categories (adjustments are net zero)
 * - Spending by category: Aggregate all transactions by category
 * - Monthly allocations: Track template-based income allocations
 * - Income allocation: Two-phase algorithm (fill to max, then percentages)
 *
 * Income allocation algorithm:
 * Phase 1: Fill categories up to max amounts proportionally
 * Phase 2: Distribute remainder by percentage settings
 *
 * @module hooks/useTransactionCalculations
 */

/**
 * Hook for transaction calculations.
 *
 * @param {Object} monthlyData - Monthly transaction data from useMonthlyData
 * @param {Array<string>} allCategoryNames - All category names
 * @param {Object|null} budgetTemplate - Budget template with max/percentage settings
 * @param {Date} selectedMonth - Currently selected month
 * @param {Object} categoryBalances - Category balances keyed by category name (from useCategoryBalances)
 * @param {boolean} fillNegativeFirst - When true, fill negative category balances first
 * @param {boolean} allowOverTarget - When true (and fillNegativeFirst=true), fill entire negative balance even if exceeding target
 * @returns {Object} Calculation functions
 * @returns {Function} return.getFilteredTransactions - Get active month transactions
 * @returns {Function} return.getTotal - Calculate net balance
 * @returns {Function} return.getSpendingByCategory - Get spending per category
 * @returns {Function} return.getMonthlyAllocations - Get allocation status (current month only)
 * @returns {Function} return.allocateIncomeByTemplate - Distribute income by template
 *
 * @example
 * const calc = useTransactionCalculations(monthlyData, categories, template, month, balances, true, false);
 * const total = calc.getTotal(); // Net balance
 * const allocations = calc.allocateIncomeByTemplate(1500); // Distribute $1500
 */
export const useTransactionCalculations = (
    monthlyData,
    allCategoryNames,
    budgetTemplate,
    selectedMonth,
    categoryBalances = {},
    fillNegativeFirst = false,
    allowOverTarget = false
) => {
    // Get filtered transactions (only the active month's data)
    const getFilteredTransactions = () => {
        return monthlyData.activeTransactions;
    };

    // Calculate total balance (only for current month)
    const getTotal = () => {
        return getFilteredTransactions().reduce((sum, transaction) => {
            if (transaction.type === 'Adjustment') {
                return sum; // Adjustments are net zero
            } else {
                const categorySum = transaction.categories.reduce((catSum, cat) => catSum + cat.amount, 0);
                return sum + categorySum;
            }
        }, 0);
    };

    // Calculate spending by category (only for current month)
    const getSpendingByCategory = () => {
        return allCategoryNames.map(cat => {
            const categoryTotal = getFilteredTransactions().reduce((sum, transaction) => {
                const categoryAmount = transaction.categories
                    .filter(transCat => transCat.category === cat)
                    .reduce((catSum, transCat) => catSum + transCat.amount, 0);
                return sum + categoryAmount;
            }, 0);
            return { category: cat, total: categoryTotal };
        });
    };

    // Calculate monthly budget allocations (only for current month)
    const getMonthlyAllocations = () => {
        if (!budgetTemplate) return null;

        const today = new Date();
        const isCurrentMonth = selectedMonth.getFullYear() === today.getFullYear() &&
            selectedMonth.getMonth() === today.getMonth();

        if (!isCurrentMonth) return null;

        const templateIncomeTransactions = getFilteredTransactions().filter(
            transaction => transaction.type === 'Income' && transaction.usedTemplate
        );

        const allocations = {};

        // Initialize all categories
        Object.keys(budgetTemplate).forEach(category => {
            allocations[category] = {
                max: budgetTemplate[category].max,
                allocated: 0,
                remaining: budgetTemplate[category].max
            };
        });

        // Calculate allocated amounts
        templateIncomeTransactions.forEach(transaction => {
            transaction.categories.forEach(cat => {
                if (allocations[cat.category]) {
                    allocations[cat.category].allocated += cat.amount;
                    allocations[cat.category].remaining = Math.max(0,
                        allocations[cat.category].max - allocations[cat.category].allocated
                    );
                }
            });
        });

        return allocations;
    };

    // Helper function to allocate income by template
    // NOTE: This function is NOT used for the transaction modal allocation preview.
    // The actual allocation logic used in the UI is in:
    //   frontend/src/components/TransactionForm/TransactionFormContainer.js
    //   -> allocateIncomeByTransactionMonthTemplate()
    // That function includes Phase 0 (fill negative categories first) logic.
    // This function is kept for backward compatibility but may be removed in the future.
    const allocateIncomeByTemplate = (totalAmount) => {
        if (!budgetTemplate) return [];

        const allocations = [];
        let remainingAmount = totalAmount;

        // Get current month's existing allocations
        const currentAllocations = getMonthlyAllocations() || {};

        // PHASE 0: Fill negative balances first (if enabled)
        if (fillNegativeFirst && remainingAmount > 0.01) {
            for (const [category, settings] of Object.entries(budgetTemplate)) {
                if (remainingAmount <= 0.01) break;

                // Find the balance for this category
                const balance = categoryBalances[category];
                if (!balance) continue;

                const endingBalance = balance.endingBalance || 0;
                if (endingBalance >= 0) continue; // Skip positive balances

                const negativeAmount = Math.abs(endingBalance);
                let fillAmount;

                if (allowOverTarget) {
                    // Fill entire negative balance
                    fillAmount = Math.min(negativeAmount, remainingAmount);
                } else {
                    // Cap at target amount
                    const target = settings.target || 0;
                    fillAmount = Math.min(negativeAmount, target, remainingAmount);
                }

                if (fillAmount > 0.01) {
                    allocations.push({ category, amount: fillAmount.toFixed(2) });
                    remainingAmount = Math.round((remainingAmount - fillAmount) * 100) / 100;

                    // Track toward target for Phase 1
                    if (!currentAllocations[category]) {
                        currentAllocations[category] = { allocated: 0 };
                    }
                    currentAllocations[category].allocated += fillAmount;
                }
            }
        }

        // PHASE 1: Fill categories by priority group (sequential processing)
        // Group categories by priorityGroup
        const priorityGroups = {};
        for (const [category, settings] of Object.entries(budgetTemplate)) {
            const group = settings.priorityGroup || 1;
            if (!priorityGroups[group]) priorityGroups[group] = [];
            priorityGroups[group].push({ category, ...settings });
        }

        // Sort groups ascending (1, 2, 3, ...)
        const sortedGroupNumbers = Object.keys(priorityGroups).map(Number).sort((a, b) => a - b);

        // Process each priority group sequentially
        for (const groupNumber of sortedGroupNumbers) {
            const groupCategories = priorityGroups[groupNumber];

            // Calculate space remaining in this group
            const categoriesWithSpace = [];
            let totalSpaceRemaining = 0;

            for (const categoryInfo of groupCategories) {
                const alreadyAllocated = currentAllocations[categoryInfo.category]?.allocated || 0;
                const spaceRemaining = Math.max(0, categoryInfo.target - alreadyAllocated);

                if (spaceRemaining > 0) {
                    categoriesWithSpace.push({
                        category: categoryInfo.category,
                        spaceRemaining,
                        targetAmount: categoryInfo.target,
                        percentage: categoryInfo.percentage
                    });
                    totalSpaceRemaining += spaceRemaining;
                }
            }

            // Allocate proportionally within this group
            if (categoriesWithSpace.length > 0 && totalSpaceRemaining > 0) {
                const amountToDistribute = Math.min(remainingAmount, totalSpaceRemaining);

                categoriesWithSpace.forEach((categoryInfo, index) => {
                    let amountForCategory;

                    if (index === categoriesWithSpace.length - 1) {
                        // Last category gets remainder (handles rounding)
                        amountForCategory = Math.min(remainingAmount, categoryInfo.spaceRemaining);
                    } else {
                        // Proportional allocation based on space remaining
                        const proportion = categoryInfo.spaceRemaining / totalSpaceRemaining;
                        amountForCategory = Math.min(
                            Math.round(proportion * amountToDistribute * 100) / 100,
                            categoryInfo.spaceRemaining,
                            remainingAmount
                        );
                    }

                    if (amountForCategory > 0.01) {
                        allocations.push({
                            category: categoryInfo.category,
                            amount: amountForCategory.toFixed(2)
                        });
                        remainingAmount = Math.round((remainingAmount - amountForCategory) * 100) / 100;
                    }
                });

                // CRITICAL: Stop if funds exhausted
                if (remainingAmount <= 0.01) break;

                // CRITICAL: Stop if this group's targets weren't fully met
                // (Don't process next group unless current group is 100% satisfied)
                if (amountToDistribute < totalSpaceRemaining) break;
            }
        }

        // PHASE 2: Distribute any remaining amount by percentages
        if (remainingAmount > 0.01) {
            const categoriesWithPercentages = Object.entries(budgetTemplate)
                .filter(([, settings]) => settings.percentage > 0);

            if (categoriesWithPercentages.length > 0) {
                const totalPercentage = categoriesWithPercentages.reduce(
                    (sum, [, settings]) => sum + settings.percentage, 0
                );

                if (totalPercentage > 0) {
                    let percentageAmountRemaining = remainingAmount;

                    categoriesWithPercentages.forEach(([category, settings], index) => {
                        if (percentageAmountRemaining <= 0.01) return;

                        let percentageAmount;
                        if (index === categoriesWithPercentages.length - 1) {
                            // Last category gets whatever is left
                            percentageAmount = percentageAmountRemaining;
                        } else {
                            percentageAmount = Math.round(
                                (settings.percentage / totalPercentage) * remainingAmount * 100
                            ) / 100;
                        }

                        // Add to existing allocation or create new one
                        const existingAllocation = allocations.find(a => a.category === category);
                        if (existingAllocation) {
                            const newAmount = parseFloat(existingAllocation.amount) + percentageAmount;
                            existingAllocation.amount = newAmount.toFixed(2);
                        } else {
                            allocations.push({
                                category,
                                amount: percentageAmount.toFixed(2)
                            });
                        }

                        percentageAmountRemaining = Math.round(
                            (percentageAmountRemaining - percentageAmount) * 100
                        ) / 100;
                    });
                }
            }
        }

        return allocations;
    };

    return {
        getFilteredTransactions,
        getTotal,
        getSpendingByCategory,
        getMonthlyAllocations,
        allocateIncomeByTemplate
    };
};
